import { useState, useEffect, useCallback } from 'react';
import { getLeads, updateLead } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { 
  Columns3, Phone, Mail, Building2, GripVertical, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const PIPELINE_STAGES = [
  { id: 'new', label: 'New', color: 'bg-slate-500' },
  { id: 'contacted', label: 'Contacted', color: 'bg-blue-500' },
  { id: 'qualified', label: 'Qualified', color: 'bg-yellow-500' },
  { id: 'proposal', label: 'Proposal', color: 'bg-purple-500' },
  { id: 'won', label: 'Won', color: 'bg-green-500' },
  { id: 'lost', label: 'Lost', color: 'bg-red-500' },
];

export const KanbanView = ({ onSelectLead }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      const res = await getLeads();
      setLeads(res.data);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => lead.status === stageId);
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', lead.id);
  };

  const handleDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverStage(null);

    if (!draggedLead || draggedLead.status === newStatus) {
      setDraggedLead(null);
      return;
    }

    // Optimistic update
    const oldStatus = draggedLead.status;
    setLeads(prevLeads => 
      prevLeads.map(l => l.id === draggedLead.id ? { ...l, status: newStatus } : l)
    );

    try {
      await updateLead(draggedLead.id, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
    } catch (err) {
      // Revert on error
      setLeads(prevLeads => 
        prevLeads.map(l => l.id === draggedLead.id ? { ...l, status: oldStatus } : l)
      );
      toast.error('Failed to update lead');
    }

    setDraggedLead(null);
  };

  const handleQuickMove = async (lead, newStatus) => {
    const oldStatus = lead.status;
    setLeads(prevLeads => 
      prevLeads.map(l => l.id === lead.id ? { ...l, status: newStatus } : l)
    );

    try {
      await updateLead(lead.id, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
    } catch (err) {
      setLeads(prevLeads => 
        prevLeads.map(l => l.id === lead.id ? { ...l, status: oldStatus } : l)
      );
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background p-4" data-testid="kanban-view">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Columns3 className="w-5 h-5 text-accent" />
          Pipeline
        </h2>
        <span className="text-sm text-muted-foreground">
          {leads.length} total leads
        </span>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto">
        <div className="flex gap-4 h-full min-w-max pb-4">
          {PIPELINE_STAGES.map(stage => {
            const stageLeads = getLeadsByStage(stage.id);
            const isDropTarget = dragOverStage === stage.id;
            const nextStageIdx = PIPELINE_STAGES.findIndex(s => s.id === stage.id) + 1;
            const nextStage = nextStageIdx < PIPELINE_STAGES.length - 1 ? PIPELINE_STAGES[nextStageIdx] : null;

            return (
              <div
                key={stage.id}
                className={`
                  w-72 flex-shrink-0 flex flex-col rounded-lg border
                  ${isDropTarget ? 'border-accent bg-accent/5' : 'bg-card'}
                `}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {/* Stage Header */}
                <div className="p-3 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <span className="font-semibold">{stage.label}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {stageLeads.length}
                  </Badge>
                </div>

                {/* Lead Cards */}
                <ScrollArea className="flex-1 p-2">
                  <div className="space-y-2">
                    {stageLeads.length === 0 ? (
                      <div className="text-center py-8 text-sm text-muted-foreground">
                        No leads
                      </div>
                    ) : (
                      stageLeads.map(lead => (
                        <div
                          key={lead.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, lead)}
                          onClick={() => onSelectLead && onSelectLead(lead)}
                          className={`
                            p-3 bg-background rounded-lg border cursor-grab
                            hover:border-accent/50 transition-all
                            ${draggedLead?.id === lead.id ? 'opacity-50' : ''}
                          `}
                        >
                          <div className="flex items-start gap-2">
                            <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Building2 className="w-4 h-4 text-accent flex-shrink-0" />
                                <span className="font-medium truncate text-sm">
                                  {lead.business_name}
                                </span>
                              </div>
                              
                              {lead.phone && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Phone className="w-3 h-3" />
                                  <span>{lead.phone}</span>
                                </div>
                              )}
                              
                              {lead.email && lead.email !== 'N/A' && (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground truncate">
                                  <Mail className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{lead.email}</span>
                                </div>
                              )}

                              {/* Quick Move Button */}
                              {nextStage && stage.id !== 'won' && stage.id !== 'lost' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full mt-2 h-7 text-xs justify-between hover:bg-accent/10"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickMove(lead, nextStage.id);
                                  }}
                                >
                                  <span>Move to {nextStage.label}</span>
                                  <ChevronRight className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {/* Stage Footer - Count Value */}
                {stage.id === 'won' && stageLeads.length > 0 && (
                  <div className="p-3 border-t text-center">
                    <span className="text-sm text-green-400 font-semibold">
                      {stageLeads.length} Won
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default KanbanView;
